/**
 * eReg Platform TypeScript Types
 */

// Protocol status
export type ProtocolStatus = 'startup' | 'active' | 'closed' | 'archived';

// Document status (P0 collapse: documents are live on upload; signature state
// lives on the signature tables, never on document status)
export type DocumentStatus = 'active' | 'archived';

// Access levels
export type AccessLevel = 'no_access' | 'read_only' | 'edit';

// User roles in delegation log
export type DelegationRole = 'PI' | 'Sub-I' | 'Site Coordinator' | 'Other';

// Signature meanings (document signing; delegation sign-offs use their own wording)
export type SignatureMeaning = 'Approved' | 'I certify this is a true and correct copy' | 'Reviewed' | 'Acknowledged';

// Network type
export interface Network {
  id: string;
  name: string;
}

// Site type
export interface Site {
  id: string;
  name: string;
  network_id: string;
  network?: Network;
}

// User type
export interface User {
  id: string;
  full_name: string;
  email: string;
  role?: string;
}

// eReg-specific protocol configuration
export interface EregProtocolConfig {
  id: string;
  protocol_id: string;
  site_id: string;
  primary_investigator_id?: string;
  primary_crc_id?: string;
  irb_of_record?: string;
  ingest_email_address?: string;
  ingest_email_token?: string;
  site_intelligence_enabled: boolean;
  ereg_status: ProtocolStatus;
  created_at: string;
  created_by?: string;
  updated_at: string;
}

// Protocol type (unified with main protocols table)
export interface Protocol {
  id: string;
  site_id: string;
  protocol_number: string;
  study_nickname?: string; // Main protocols table uses study_nickname
  protocol_title?: string; // Alias for backward compatibility
  sponsor_name?: string;
  therapeutic_area?: string;
  phase?: string;
  status?: string; // Main protocols table status (active/inactive/completed)
  created_at: string;
  updated_at: string;
  site?: Site;

  // eReg-specific config (from ereg_protocol_config)
  ereg_config?: EregProtocolConfig;

  // Convenience accessors (populated from ereg_config or flat structure)
  primary_investigator_id?: string;
  primary_crc_id?: string;
  irb_of_record?: string;
  ingest_email_address?: string;
  site_intelligence_enabled?: boolean;
  created_by?: string;
  primary_investigator?: User;
  primary_crc?: User;
}

// Helper to get eReg status from protocol
export function getEregStatus(protocol: Protocol): ProtocolStatus {
  return protocol.ereg_config?.ereg_status || 'startup';
}

// Helper to get protocol title (handles both field names)
export function getProtocolTitle(protocol: Protocol): string | undefined {
  return protocol.ereg_config
    ? protocol.study_nickname
    : (protocol.protocol_title || protocol.study_nickname);
}

// Folder type
export interface Folder {
  id: string;
  protocol_id?: string;
  site_id: string;
  parent_folder_id?: string;
  name: string;
  path: string;
  allowed_document_types?: string[];
  default_document_type?: string;
  default_expiration_tracking: boolean;
  sort_order: number;
  created_at: string;
  created_by?: string;
  children?: Folder[];
  document_count?: number;
  placeholder_count?: number;
}

// Document type
export interface Document {
  id: string;
  folder_id: string;
  protocol_id?: string;
  site_id: string;
  document_name: string;
  document_type: string;
  document_date?: string;
  version_number: string;
  status: DocumentStatus;
  s3_key: string;
  s3_bucket: string;
  file_size_bytes?: number;
  mime_type?: string;
  original_filename?: string;
  signed_s3_key?: string;
  signed_at?: string;
  document_hash?: string;
  signed_document_hash?: string;
  expiration_date?: string;
  document_owner_id?: string;
  description?: string;
  source_document_id?: string;
  is_frozen: boolean;
  fulfills_placeholder_id?: string;
  created_at: string;
  created_by?: string;
  updated_at: string;
  updated_by?: string;
  deleted_at?: string;
  deleted_by?: string;
  folder?: Folder;
  document_owner?: User;
  created_by_user?: User;
  signatures?: Signature[];
  signature_requests?: Array<{ id: string; status: SignatureRequest['status'] }>;
  /** Key-document slots this document occupies (plan §3.2). */
  key_document_slots?: Array<{ id: string; role: string; variant_label: string }>;
}

// Derived signature state for a document (drives the row chips)
export type DocumentSignatureState =
  | 'signed'                  // current version fully signed
  | 'pending'                 // open signature request
  | 'newer_version_unsigned'  // a prior version was signed, the current one is not
  | 'none';

export function getDocumentSignatureState(doc: Document): DocumentSignatureState {
  // signed_at is authoritative — the backend sets it only when EVERY assignment
  // on the current version is signed.
  if (doc.signed_at) return 'signed';
  if ((doc.signature_requests || []).some(r => r.status === 'pending')) return 'pending';
  const sigs = doc.signatures || [];
  // Signatures on the current version without signed_at = partially signed
  // (e.g. a co-signer declined or the request expired) — still pending, not signed.
  if (sigs.some(s => s.version_number === doc.version_number)) return 'pending';
  if (sigs.length > 0) return 'newer_version_unsigned';
  return 'none';
}

// Document version type
export interface DocumentVersion {
  id: string;
  document_id: string;
  version_number: string;
  s3_key: string;
  s3_bucket: string;
  document_hash?: string;
  created_at: string;
  created_by?: string;
  superseded_at?: string;
  superseded_by?: string;
}

// Signature type (sign-the-version: version_number records the document version signed)
export interface Signature {
  id: string;
  document_id?: string; // null for delegation sign-offs
  signer_id: string;
  signer_name: string;
  signer_email: string;
  signature_meaning: string;
  signed_at: string;
  request_id?: string;
  version_number?: string;
  document_hash_at_signing?: string;
  ip_address?: string;
  user_agent?: string;
  signature_image_s3_key?: string;
  page_number?: number;
  x_position?: number;
  y_position?: number;
}

// Signature request type
export interface SignatureRequest {
  id: string;
  document_id: string;
  protocol_id?: string;
  site_id?: string;
  status: 'pending' | 'completed' | 'expired' | 'cancelled';
  message?: string;
  due_date?: string;
  requested_by?: string;
  created_at: string;
  completed_at?: string;
  document?: Document;
}

// Sign response type
export interface SignResponse {
  signature: Signature;
  allSigned: boolean;
  signedHash: string;
}

// Batch sign response type
export interface BatchSignResponse {
  results: Array<{
    assignmentId: string;
    signatureId: string;
    documentId: string;
    success: boolean;
  }>;
  errors: Array<{
    assignmentId: string;
    error: string;
  }>;
  totalSigned: number;
  totalErrors: number;
}

// Signature placement type
export interface SignaturePlacement {
  page_number: number;
  x_position: number;
  y_position: number;
  signer_id: string;
  signature_meaning: SignatureMeaning;
}

// Signature assignment type
export interface SignatureAssignment {
  id: string;
  request_id: string;
  signer_id: string;
  signature_meaning: SignatureMeaning;
  order_number: number;
  status: 'pending' | 'viewed' | 'signed' | 'declined' | 'expired';
  viewed_at?: string;
  signed_at?: string;
  declined_at?: string;
  decline_reason?: string;
  signature_id?: string;
  created_at: string;
  signature_request?: SignatureRequest;
}

// Signature queue item (assignment with full document info)
export interface SignatureQueueItem extends SignatureAssignment {
  signature_request: SignatureRequestWithDocument;
}

// Signature request with document details
export interface SignatureRequestWithDocument extends SignatureRequest {
  document: Document & {
    folder?: Folder;
    protocol?: Protocol;
  };
  requested_by_user?: User;
}

// Placeholder type
export interface Placeholder {
  id: string;
  folder_id: string;
  protocol_id?: string;
  name: string;
  instructions?: string;
  expected_document_type?: string;
  for_user_id?: string;
  for_delegate_role?: string;
  requires_signature: boolean;
  is_fulfilled: boolean;
  fulfilled_by_document_id?: string;
  fulfilled_at?: string;
  is_overridden: boolean;
  override_reason?: string;
  overridden_at?: string;
  overridden_by?: string;
  created_at: string;
  created_by?: string;
}

// Document type definition
export interface DocumentType {
  id: string;
  name: string;
  is_global: boolean;
  network_id?: string;
  site_id?: string;
  sort_order: number;
}

// Folder template type
export interface FolderTemplate {
  id: string;
  name: string;
  scope: 'global' | 'network' | 'site';
  scope_id?: string;
  structure: FolderTemplateItem[];
  is_site_template: boolean;
  created_at: string;
  created_by?: string;
}

// Folder template item structure
export interface FolderTemplateItem {
  name: string;
  allowed_document_types?: string[];
  default_document_type?: string;
  sort_order?: number;
  children?: FolderTemplateItem[];
  placeholders?: PlaceholderTemplate[];
}

// Placeholder template
export interface PlaceholderTemplate {
  name: string;
  instructions?: string;
  document_type?: string;
  requires_signature?: boolean;
}

// Audit trail entry
export interface AuditTrailEntry {
  id: string;
  entity_type: string;
  entity_id: string;
  document_id?: string;
  protocol_id?: string;
  site_id?: string;
  action: string;
  action_details?: Record<string, unknown>;
  user_id?: string;
  user_email?: string;
  ip_address?: string;
  created_at: string;
}

// Protocol access type
export interface ProtocolAccess {
  id: string;
  protocol_id: string;
  user_id: string;
  access_level: AccessLevel;
  granted_at: string;
  granted_by?: string;
  user?: User;
}

// AI metadata suggestion response
export interface MetadataSuggestion {
  document_name: string;
  document_type: string | null;
  suggested_folder?: string;
  confidence: number;
}

// Document view/download URL response
export interface DocumentUrlResponse {
  url: string;
  contentType?: string;
  filename?: string;
  viewable?: boolean;
}

// Dashboard stats
export interface DashboardStats {
  pending_signatures: number;
  missing_documents: number;
  expiring_documents: number;
  protocols: ProtocolSummary[];
}

// Protocol summary for dashboard
export interface ProtocolSummary {
  id: string;
  protocol_number: string;
  status: ProtocolStatus;
  placeholder_total: number;
  placeholder_fulfilled: number;
  expiring_count: number;
  last_activity: string;
}

// =====================================================
// DELEGATION LOG TYPES
// =====================================================

// Delegation entry status
export type DelegationEntryStatus =
  | 'pending_cert_review'
  | 'pending_signatures'
  | 'active'
  | 'ended'
  | 'terminated';

// Delegation log
export interface DelegationLog {
  id: string;
  protocol_id: string;
  pdf_config?: Record<string, unknown>;
  status: 'active' | 'closed';
  pi_closeout_signature_id?: string;
  published_pdf_document_id?: string;
  published_pdf_version: number;
  created_at: string;
  created_by?: string;
  updated_at: string;
  tasks?: DelegationTask[];
  entries?: DelegationEntry[];
}

// Delegation task
export interface DelegationTask {
  id: string;
  delegation_log_id: string;
  task_number: number;
  task_description: string;
  required_document_types: string[];
  sort_order: number;
}

// Delegation entry (team member)
export interface DelegationEntry {
  id: string;
  delegation_log_id: string;
  user_id?: string;
  external_name?: string;
  external_email?: string;
  invitation_sent_at?: string;
  invitation_accepted_at?: string;
  role: string;
  role_other?: string;
  delegated_task_ids: string[];
  start_date: string;
  end_date?: string;
  status: DelegationEntryStatus;
  team_member_start_signature_id?: string;
  pi_start_signature_id?: string;
  team_member_end_signature_id?: string;
  pi_end_signature_id?: string;
  has_override: boolean;
  override_reason?: string;
  override_at?: string;
  override_by?: string;
  created_at: string;
  created_by?: string;
  updated_at: string;
  user?: User;
  required_docs?: DelegationRequiredDoc[];
  team_member_start_signature?: Pick<Signature, 'id' | 'signer_name' | 'signature_meaning' | 'signed_at'>;
  pi_start_signature?: Pick<Signature, 'id' | 'signer_name' | 'signature_meaning' | 'signed_at'>;
}

// Required document for delegation entry
export interface DelegationRequiredDoc {
  id: string;
  delegation_entry_id: string;
  task_id: string;
  document_type: string;
  document_id?: string;
  status: 'missing' | 'pending_approval' | 'approved';
  document?: Document;
}

// =====================================================
// EMAIL INGESTION TYPES
// =====================================================

// Ingest queue item status
export type IngestQueueStatus = 'unfiled' | 'filed' | 'rejected';

// Ingest queue item
export interface IngestQueueItem {
  id: string;
  protocol_id: string;
  source_type: 'email' | 'upload';
  source_email_from?: string;
  source_email_subject?: string;
  source_email_body?: string;
  source_email_s3_key?: string;
  original_filename: string;
  s3_key: string;
  s3_bucket: string;
  file_size_bytes?: number;
  mime_type?: string;
  suggested_document_name?: string;
  suggested_document_type?: string;
  suggested_folder_id?: string;
  suggested_folder?: Folder;
  status: IngestQueueStatus;
  filed_at?: string;
  filed_by?: string;
  filed_as_document_id?: string;
  filed_as_document?: Document;
  include_body_as_pdf: boolean;
  body_pdf_s3_key?: string;
  warnings?: string[];
  created_at: string;
}

// Dead letter queue item
export interface DeadLetterItem {
  id: string;
  raw_email_s3_key: string;
  received_at: string;
  from_email?: string;
  to_email?: string;
  subject?: string;
  rejection_reason: string;
  reviewed_at?: string;
  reviewed_by?: string;
  created_at: string;
}

// Metadata suggestion response
export interface MetadataSuggestionResponse {
  document_name: string;
  document_type: string | null;
  folder_id: string | null;
  confidence: number;
}

// File document request
export interface FileDocumentRequest {
  folder_id: string;
  document_name: string;
  document_type: string;
  document_date?: string;
  version_number?: string;
  expiration_date?: string;
  description?: string;
  include_body_as_pdf?: boolean;
}

// (Intelligence copy-sync types removed in P3 — Intelligence reads the
// registry directly; plan §5.2.)

// =====================================================
// SEARCH TYPES
// =====================================================

export interface SearchResult {
  id: string;
  document_name: string;
  document_type: string;
  status: DocumentStatus;
  version_number: string;
  created_at: string;
  folder?: Folder;
  protocol?: Protocol;
}

export interface SearchResponse {
  query: string;
  count: number;
  results: SearchResult[];
}

// =====================================================
// DASHBOARD TYPES
// =====================================================

export interface DashboardStatsResponse {
  pending_signatures: number;
  missing_documents: number;
  expiring_documents: number;
  unfiled_emails: number;
  protocols: ProtocolStat[];
}

export interface ProtocolStat {
  id: string;
  protocol_number: string;
  status: ProtocolStatus;
  placeholder_total: number;
  placeholder_fulfilled: number;
  expiring_count: number;
}
